import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, 
  User, 
  FileText, 
  Globe, 
  ChevronDown,
  Moon,
  Sun,
  BookOpen,
  Disc3,
  History
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface AppHeaderProps {
  lang: string;
  setLang: (lang: any) => void;
  onLogout: () => void;
  theme?: 'light' | 'dark';
  onToggleTheme?: () => void;
  onOpenSavedPdfs?: () => void;
  onOpenRecentCalcs?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = React.memo(({ 
  lang, 
  setLang, 
  onLogout, 
  theme = 'light',
  onToggleTheme,
  onOpenSavedPdfs,
  onOpenRecentCalcs
}) => {
  const [isLangOpen, setIsLangOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languages = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'zh', label: '中文', flag: '🇨🇳' },
    { code: 'ja', label: '日本語', flag: '🇯🇵' },
    { code: 'ko', label: '한국어', flag: '🇰🇷' },
  ];

  const currentLang = languages.find(l => l.code === lang) || languages[0];

  return (
    <header className="h-16 lg:h-20 bg-white dark:bg-[#182235]/95 border-b border-slate-200 dark:border-slate-400/20 sticky top-0 z-[100] transition-colors duration-300 backdrop-blur-md dark:backdrop-blur-xl">
      <div className="max-w-[1920px] h-full mx-auto px-3 sm:px-4 lg:px-6 flex items-center justify-between relative">
        
        {/* Left: Theme, Language, PDF Reports (3 items) */}
        <div className="flex items-center gap-1 sm:gap-2 z-10">
          {/* Theme Toggle */}
          {onToggleTheme && (
            <button 
              onClick={onToggleTheme}
              className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 text-slate-500 dark:text-slate-400 transition-colors"
              title="Toggle Theme"
            >
              {theme === 'light' ? <Moon className="w-4 h-4 sm:w-5 sm:h-5" /> : <Sun className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          )}

          {/* Language Switcher */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 text-slate-600 dark:text-slate-300 transition-colors text-xs sm:text-sm font-medium border border-transparent hover:border-slate-200 dark:hover:border-white/10"
            >
              <Globe className="w-4 h-4 text-orange-500" />
              <span className="hidden sm:inline">{currentLang.label}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isLangOpen ? 'rotate-180' : ''}`} />
            </button>

            {isLangOpen && (
              <div className="absolute left-0 mt-2 w-44 sm:w-48 bg-white dark:bg-[#0D111C] rounded-xl shadow-2xl border border-slate-200 dark:border-white/10 overflow-hidden animate-in fade-in zoom-in-95 duration-200 z-[110]">
                <div className="p-1">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => {
                        setLang(l.code);
                        setIsLangOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        lang === l.code 
                          ? 'bg-orange-50 text-orange-600 dark:bg-orange-600/10 dark:text-orange-400 font-bold' 
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5'
                      }`}
                    >
                      <span>{l.flag}</span>
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Reports / Surveys */}
          <button onClick={() => navigate('/surveys')} className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 text-slate-500 dark:text-slate-400 transition-colors group" title={lang === 'tr' ? 'Raporlar & Saha Kontrolü' : 'Reports'}>
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 group-hover:text-orange-500 transition-colors text-orange-500/80 dark:text-orange-400/80" />
          </button>

          {/* Recent 5 Calculations */}
          {onOpenRecentCalcs && (
            <button 
              onClick={onOpenRecentCalcs} 
              className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 text-slate-500 dark:text-slate-400 transition-colors group" 
              title={lang === 'tr' ? 'Son 5 Hesaplama (Salt Okunur)' : 'Recent 5 Calculations'}
            >
              <History className="w-4 h-4 sm:w-5 sm:h-5 group-hover:text-orange-500 transition-colors" />
            </button>
          )}

          <button onClick={() => navigate('/app/steel-coil')} className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-orange-50 hover:text-orange-700 dark:text-slate-300 dark:hover:bg-orange-500/10 dark:hover:text-orange-300" title="Steel Coil Calculator">
            <Disc3 className="h-4 w-4 text-orange-500" />
            <span className="hidden xl:inline">Steel Coil</span>
          </button>

          <span className="text-slate-400 font-medium tracking-wide uppercase text-[10px] whitespace-nowrap hidden lg:block ml-2">
            {new Date().toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>

        {/* Center: Enlarged Centered Logo */}
        <div 
          onClick={() => navigate('/')}
          className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 flex items-center gap-2 cursor-pointer hover:opacity-95 transition-all z-20"
        >
          <img src="/sonlogo.png" alt="Logo" className="h-9 sm:h-12 lg:h-15 w-auto object-contain drop-shadow-md transition-transform hover:scale-105" />
          <span className="hidden xl:inline font-bold text-slate-800 dark:text-white text-base lg:text-lg tracking-tight whitespace-nowrap">
            {t('lashing_calculator')}
          </span>
        </div>

        {/* Right: Manual, Profile, Logout (3 items) */}
        <div className="flex items-center gap-1 sm:gap-2 z-10">
          <button onClick={() => navigate('/manual')} className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 text-slate-500 dark:text-slate-400 transition-colors group" title="User Manual">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 group-hover:text-orange-500 transition-colors" />
          </button>
          <button onClick={() => navigate('/profile')} className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 text-slate-500 dark:text-slate-400 transition-colors group" title="Profile">
            <User className="w-4 h-4 sm:w-5 sm:h-5 group-hover:text-orange-500 transition-colors" />
          </button>
          <button 
            onClick={onLogout}
            className="p-1.5 sm:p-2 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-600/10 text-slate-500 hover:text-rose-600 transition-colors group"
            title="Logout"
          >
            <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>
    </header>
  );
});

AppHeader.displayName = 'AppHeader';

export default AppHeader;
